import type {
  ActionResult,
  StartChatOptions,
} from '@wandb/forge-shell-contract/aria';

import {envIsDev, envIsTest} from '../../../config';
import type {ContentPart} from '../store/thread';

/** UnavailableReason explains why the agent chat feature is unavailable. */
export type UnavailableReason =
  | 'feature-flag-disabled'
  | 'not-public-cloud'
  | 'logged-out'
  | 'personal-entity'
  | 'org-setting-disabled'
  | 'loading';

/** AgentChatAvailability reports whether the agent chat feature is enabled. */
export interface AgentChatAvailability {
  /** Whether the feature is enabled (gate + org settings pass). */
  isAvailable: boolean;
  unavailableReason?: UnavailableReason;
}

/**
 * WBAgentController is the set of agent chat capabilities exposed to the rest
 * of the app, such as opening the chat or sending a message.
 */
export interface WBAgentController {
  /**
   * startChat delivers a prompt to chat. It reports `readonly` when the
   * chat is locked, `empty` when there is nothing to send, and `occupied` when
   * an existing draft prevents an editable prompt from being set; the caller
   * doesn't need to check access first.
   *
   * `target: 'active-or-new'` targets the active chat, or creates one when none
   * exists. `delivery: 'send'` sends immediately; `delivery: 'draft'` updates
   * the composer. Drafts targeting an active chat append their parts without
   * replacing existing content. A draft may provide only `placeholder`, which
   * guides the user without creating draft content. `entryPoint`, when
   * provided, records which external surface started the chat.
   */
  startChat(
    // A string prompt (optionally with leading `attachments`), or a full
    // ordered list of parts — the composite form that lets a prompt carry
    // structured context (a conversation reference, inline mentions) beside
    // its text.
    prompt: string | ContentPart[],
    opts: StartChatOptions
  ): ActionResult<'readonly' | 'empty' | 'occupied'>;
  openChat(): void;
  openChatToThread(threadId: string): void;
}

/**
 * WBAgentControllerStore holds the agent chat's feature availability and the
 * imperative handle used to open the chat or send a message. One instance is
 * shared app-wide; tests instantiate their own for isolation.
 *
 * NOTE: Methods use arrow functions so they are bound; important when passing
 * to `useSyncExternalStore`.
 */
export class WBAgentControllerStore {
  private availability: AgentChatAvailability = {
    isAvailable: false,
    unavailableReason: 'loading',
  };

  private availabilityRegistration: object | null = null;

  private handle: WBAgentController | null = null;

  private readonly listeners = new Set<() => void>();

  getAvailability = (): AgentChatAvailability => this.availability;

  setAvailability = (next: AgentChatAvailability): void => {
    if (next === this.availability) {
      return;
    }
    this.availability = next;
    this.emit();
  };

  /** Publishes availability; only this registration's cleanup can reset it. */
  registerAvailability = (next: AgentChatAvailability): (() => void) => {
    const registration = {};
    this.availabilityRegistration = registration;
    this.setAvailability(next);

    return () => {
      if (this.availabilityRegistration !== registration) {
        return;
      }
      this.availabilityRegistration = null;
      this.setAvailability({
        isAvailable: false,
        unavailableReason: 'loading',
      });
    };
  };

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * registerHandle installs the imperative handle and returns a disposer. Only
   * one handle is expected at a time (a single mount point); a second
   * registration in dev signals a duplicate mount.
   */
  registerHandle = (next: WBAgentController): (() => void) => {
    if (this.handle != null && (envIsDev || envIsTest)) {
      console.warn(
        'WBAgentController handle registered twice; expected a single agent chat mount point.'
      );
    }

    this.handle = next;
    return () => {
      if (this.handle === next) {
        this.handle = null;
      }
    };
  };

  getHandle = (): WBAgentController | null => this.handle;

  private emit(): void {
    // NOTE: Snapshot listeners to avoid mutation during iteration.
    for (const listener of Array.from(this.listeners)) {
      listener();
    }
  }
}

/** wbAgentControllerStore is the shared, app-wide controller store instance. */
export const wbAgentControllerStore = new WBAgentControllerStore();
